"use client";
import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useCallback,
} from "react";
import {
  IconArrowNarrowLeft,
  IconArrowNarrowRight,
  IconX,
} from "@tabler/icons-react";
import { MdVolumeUp } from "react-icons/md";
import { cn } from "../../utils/cn";
import { AnimatePresence, motion } from "framer-motion";

// CarouselContext
export const CarouselContext = createContext({
  onCardClose: () => { },
  currentIndex: 0,
});

// Carousel Component
export const Carousel = ({ items, initialScroll = 0, autoScroll = false, autoScrollInterval = 5000, gap = "gap-4" }) => {
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const isMobile = () => {
    return typeof window !== "undefined" && window.innerWidth < 768;
  };

  const getScrollStep = () => {
    return isMobile() ? 240 : 528; // Card width + gap
  };

  const checkScrollability = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 20);
    }
  }, []);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll, checkScrollability]);

  const [direction, setDirection] = useState('right');

  useEffect(() => {
    if (isHovered || !autoScroll) return;

    const interval = setInterval(() => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        const step = getScrollStep();

        if (direction === 'right') {
          if (scrollLeft + clientWidth >= scrollWidth - 50) {
            setDirection('left');
            carouselRef.current.scrollBy({ left: -step, behavior: "smooth" });
          } else {
            carouselRef.current.scrollBy({ left: step, behavior: "smooth" });
          }
        } else {
          if (scrollLeft <= 10) {
            setDirection('right');
            carouselRef.current.scrollBy({ left: step, behavior: "smooth" });
          } else {
            carouselRef.current.scrollBy({ left: -step, behavior: "smooth" });
          }
        }
      }
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [isHovered, autoScrollInterval, direction]);

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -getScrollStep(), behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: getScrollStep(), behavior: "smooth" });
    }
  };

  const handleCardClose = (index) => {
    if (carouselRef.current) {
      const step = getScrollStep();
      const scrollPosition = step * index;
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <CarouselContext.Provider value={{ onCardClose: handleCardClose }}>
      <div
        className="relative w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="flex w-full overflow-x-scroll overscroll-x-auto py-8 md:py-12 scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          ref={carouselRef}
          onScroll={checkScrollability}
        >
          <div
            className={cn(
              "flex flex-row justify-start pl-4",
              gap
            )}
          >
            {items.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.8,
                    delay: 0.1 * index,
                    ease: [0.215, 0.61, 0.355, 1]
                  }
                }}
                viewport={{ once: true, amount: 0.2 }}
                key={"card" + index}
                className="last:pr-[33%] md:last:pr-[33%] rounded-[2.5rem]"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-5 mr-10 md:mr-20">
          <button
            className="relative z-40 h-14 w-14 rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center disabled:opacity-20 shadow-2xl transition-all hover:scale-110 active:scale-95 border border-zinc-200 dark:border-zinc-700"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
          >
            <IconArrowNarrowLeft className="h-7 w-7 text-zinc-600 dark:text-zinc-200" />
          </button>
          <button
            className="relative z-40 h-14 w-14 rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center disabled:opacity-20 shadow-2xl transition-all hover:scale-110 active:scale-95 border border-zinc-200 dark:border-zinc-700"
            onClick={scrollRight}
            disabled={!canScrollRight}
          >
            <IconArrowNarrowRight className="h-7 w-7 text-zinc-600 dark:text-zinc-200" />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

// Card Component
export const Card = ({ card, index, layout = false }) => {
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const { onCardClose } = useContext(CarouselContext);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        handleClose();
      }
    }

    if (open) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0px";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0px";
    };
  }, [open]);

  useEffect(() => {
    if (isHovered && videoRef.current) {
      videoRef.current.play().catch(() => { });
    } else if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovered]);

  useOutsideClick(containerRef, () => handleClose());

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onCardClose(index);
  };

  const youtubeId = getYoutubeVideoId(card.video);

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-black/90 backdrop-blur-2xl h-full w-full fixed inset-0"
              onClick={handleClose}
            />
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              ref={containerRef}
              layoutId={layout ? `card-${card.title}` : undefined}
              className="max-w-5xl w-full max-h-[90vh] bg-white dark:bg-neutral-900 z-[110] p-4 md:p-10 rounded-3xl font-sans relative shadow-2xl overflow-y-auto scrollbar-hide"
            >
              <button
                className="sticky top-0 right-0 ml-auto h-8 w-8 bg-black dark:bg-white rounded-full flex items-center justify-center z-50 transition-transform hover:scale-110 active:scale-95"
                onClick={handleClose}
              >
                <IconX className="h-5 w-5 text-white dark:text-black" />
              </button>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <div>
                  <motion.p
                    layoutId={layout ? `category-${card.title}` : undefined}
                    className="text-base font-medium text-blue-600 dark:text-blue-400"
                  >
                    {card.category}
                  </motion.p>
                  <motion.p
                    layoutId={layout ? `title-${card.title}` : undefined}
                    className="text-2xl md:text-4xl font-bold text-neutral-900 mt-2 dark:text-white"
                  >
                    {card.title}
                  </motion.p>
                </div>
              </div>
              {/* Modal Video */}
              {card.video && (
                <div className="w-full lg:rounded-3xl rounded-xl overflow-hidden lg:mt-4 mt-2.5 aspect-video h-48 md:h-80 shadow-2xl">
                  {youtubeId ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full object-cover"
                    ></iframe>
                  ) : (
                    <video
                      src={card.video}
                      controls
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}

              <div className="lg:py-6 py-4">{card.content}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <motion.button
        layoutId={layout ? `card-${card.title}` : undefined}
        onClick={handleOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        whileHover={{ scale: 1.02 }}
        className="lg:rounded-2xl rounded-xl bg-gray-100 dark:bg-neutral-900 h-64 w-52 md:h-80 md:w-[28rem] overflow-hidden flex flex-col items-start justify-start relative z-10 transition-shadow hover:shadow-2xl"
      >
        {/* Background Video */}
        {card.video && (
          <div className={cn(
            "absolute inset-0 z-20 transition-all duration-1000 ease-out",
            isHovered ? "opacity-100 scale-110" : "opacity-0 scale-100"
          )}>
            {youtubeId ? (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${youtubeId}&showinfo=0&rel=0`}
                title="Project Video"
                frameBorder="0"
                allow="autoplay; encrypted-media"
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : (
              <video
                ref={videoRef}
                src={card.video}
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            )}
            {/* Direct video overlay for extra contrast */}
            <div className="absolute inset-0 bg-black/40 z-[21]" />
          </div>
        )}

        {/* Gradients */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-30 opacity-60 pointer-events-none" />
        <div className={cn(
          "absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent z-30 transition-opacity duration-700 pointer-events-none",
          isHovered ? "opacity-100" : "opacity-0"
        )} />

        {/* Card Header */}
        <div className="relative z-40 p-4 md:p-6 w-full h-full flex flex-col justify-between">
          <div className="flex flex-col items-start w-full">


            <motion.p
              layoutId={layout ? `category-${card.category}` : undefined}
              className="text-white/80 text-sm md:text-base font-medium font-sans text-left tracking-wider uppercase"
            >
              {card.category}
            </motion.p>
            <motion.p
              layoutId={layout ? `title-${card.title}` : undefined}
              className="text-white text-2xl md:text-5xl font-bold max-w-xs text-left [text-wrap:balance] font-sans lg:mt-3 mt-0.5 leading-tight"
            >
              {card.title}
            </motion.p>
          </div>

          {/* Hover Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 20 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col gap-4 text-left"
          >
            <p className="text-white/70 text-sm md:text-lg font-light line-clamp-3 leading-relaxed">
              {card.shortDescription}
            </p>
            <div className="flex flex-wrap gap-2">
              {card.techStack?.slice(0, 4).map((tech, i) => (
                <span key={i} className="text-[10px] md:text-xs font-semibold px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-white border border-white/20">
                  {tech.name}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        <BlurImage
          src={card.src}
          alt={card.title}
          className={cn(
            "object-cover absolute z-10 inset-0 w-full h-full transition-transform duration-700",
            isHovered ? "scale-110 blur-none" : "scale-100 blur-sm"
          )}
        />
        {card.customImage && (
          <div className="absolute inset-0 z-10 w-full h-full">
            {card.customImage}
          </div>
        )}

        {/* Speaker Icon - Show on hover only */}
        {card.video && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="absolute top-3 right-3 z-30 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-2 rounded-full transition-all cursor-pointer pointer-events-auto"
            whileHover={{ scale: 1.1 }}
          >
            <MdVolumeUp size={18} />
          </motion.div>
        )}
      </motion.button>
    </>
  );
};

// BlurImage Component
export const BlurImage = ({ src, className, alt, ...rest }) => {
  const [isLoading, setLoading] = useState(true);
  return (
    <img
      className={cn(
        "transition duration-300",
        isLoading ? "blur-sm" : "blur-0",
        className
      )}
      onLoad={() => setLoading(false)}
      src={src}
      loading="lazy"
      alt={alt ? alt : "Project Preview"}
      {...rest}
    />
  );
};

// useOutsideClick Hook
export const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      callback(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, callback]);
};

function getYoutubeVideoId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url?.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}
